import i18next, {ParseKeys, DefaultNamespace, Namespace, TOptions} from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import {initReactI18next, Trans as i18nTrans, TransProps} from 'react-i18next';
import type {ReactElement} from 'react';

import {isTestEnv} from 'common/report/util/env';
import {getFlattenedDictionary} from 'common/i18n/util';
import {reporter} from 'common/report/error-reporting';
import {localStorageManager} from 'common/contexts/storage-manager';

import type {LocalizationStore} from './store';
import {LOCALES} from './locales';
import en from './locales/en';

const STORAGE_KEY = `selectedLanguage`;
const DEFAULT_NS = `translation`;

function getDefaultLocale() {
  const stored = typeof window !== `undefined` ? localStorageManager.getItem(STORAGE_KEY) : null;
  if (!isTestEnv() && stored) {
    return stored;
  }

  return `en`;
}

i18next
  .use(
    resourcesToBackend(async (language: string) => {
      const [generated, overrides] = await Promise.all([
        // eslint-disable-next-line no-unsanitized/method
        import(`./locales/generated/${language}.json`).then((module) => {
          const translations = module.default;

          return Object.fromEntries(
            Object.entries(translations).map(([key, value]) => {
              // removing the hash off the end of each key
              const keyWithoutHash = key.split(`_`).slice(0, -1).join(`_`);

              return [keyWithoutHash, value];
            }),
          );
        }),
        // eslint-disable-next-line no-unsanitized/method
        import(`./locales/${language}.ts`).then((module) => module.default).catch(() => ({})),
      ]);

      return {
        ...generated,
        ...overrides,
      };
    }),
  )
  .use(initReactI18next) // bind react-i18next to the instance
  .init({
    lng: getDefaultLocale(),
    fallbackLng: `en`,
    // not supporting saving missing, but needs to be true to enable missingKey event
    saveMissing: true,
    defaultNS: DEFAULT_NS,
    interpolation: {
      // Translated text with user input is never rendered directly in the dom (React already handles xss sanitization).
      // Double-escaping leads to rendering issues for names with special characters
      escapeValue: false,
    },
  });

// https://www.i18next.com/translation-function/formatting#adding-custom-format-function
i18next.services.formatter?.add(`lowercase`, (value) => {
  return value.toLowerCase();
});

// we always want English to be available immediately
i18next.addResourceBundle(`en`, DEFAULT_NS, getFlattenedDictionary(en, false));

i18next.on(`languageChanged`, (lng) => {
  if (typeof window !== `undefined`) {
    localStorageManager.setItem(STORAGE_KEY, lng);
  }
});

i18next.on(`missingKey`, (_lng, _namespace, key, _fallbackValue) => {
  reporter.error(`Missing i18n entry for ${key}`);
});

export function changeLanguage(code: string) {
  i18next.changeLanguage(code);
}

/**
 * Checks if an i18n key exists.
 *
 * NOTE: This should only be used for dynamic keys that cannot be determined at compile time (e.g. keys that are sent from a server). Otherwise, keys should be statically typed and enforced by TS.
 */
export function doesKeyExist(key: string): key is TranslationKey {
  return i18next.exists(key as any);
}

// this is a little hack that makes i18next conditional types work w/o strictNullChecks enabled
type KeyPrefix = 0;

// eslint-disable-next-line @typescript-eslint/no-restricted-types, @typescript-eslint/no-empty-object-type
export type TranslationKey = ParseKeys<typeof DEFAULT_NS, {}, KeyPrefix>;
export type Translatable = string | ((i18n: LocalizationStore) => string);

// // if we ever get to strictNullChecks, can remove this retyping and use react-i18next's type
type TransFunc = <
  Key extends ParseKeys<Ns, TOpt, KeyPrefix>,
  Ns extends Namespace = DefaultNamespace,
  KPrefix = KeyPrefix,
  TContext extends string | undefined = undefined,
  TOpt extends TOptions & {context?: TContext} = {context: TContext},
  E = React.HTMLProps<HTMLDivElement>,
>(
  props: Omit<TransProps<Key, Ns, KPrefix, TContext, TOpt, E>, `i18nKey` | `t`> & {
    i18nKey: TranslationKey;
    t: LocalizationStore[`t`];
  },
) => ReactElement;

export const Trans = i18nTrans as TransFunc;

export {i18next, LOCALES};
